import { useState, useEffect, useRef } from "react";
import { useNavigate, useParams } from "react-router";
import useSWR, { useSWRConfig } from "swr";
import { fetcherSimple, fetcherFull } from "utils/api";
import { getApiErrorMessage, classifyError } from "utils/api-error";
import { API_SERVER } from "utils/config";
import { toast } from "react-toastify";
import ContentWrapper from "components/ui/content-wrapper";
import UploadPicture from "assets/img/logos/gobby-logo-FIN_Stacked-BL-wTag-.png";
import { ResponseWrapper } from "components/ui/response-wrapper";
import Image from "components/image";
import {
    Alert,
    Button,
    Card,
    CardBody,
    CardFooter,
    CardHeader,
    IconButton,
    Progress,
    Typography,
} from "@material-tailwind/react";
import MaterialIcon from "components/material-icon";
import CustomQuestionResponse from "components/custom-question-response";
import PresetQuestionResponse from "components/preset-question-response";

export default function RespondSurvey() {
    const { surveyNanoid } = useParams();
    const navigate = useNavigate();

    const [previewModeChecked, setPreviewModeChecked] = useState(false);
    const [previewModeActive, setPreviewModeActive] = useState(false);
    const [logoAvailable, setLogoAvailable] = useState(true);
    const [openSurvey, setOpenSurvey] = useState(false);
    const [validationErrors, setValidationErrors] = useState(null);

    const headerContainerRef = useRef(null);
    const rightImageContainerRef = useRef(null);
    // Auth check: preview-mode is true when an authenticated survey-builder
    // user is previewing the survey.
    const { data: previewMode, error: previewModeError } = useSWR(
        () => ["/v2/auth/session", "GET"],
        ([path, method]) => fetcherSimple(path, method),
    );
    const { data: auth, error: authError } = useSWR(
        () => [`/v2/response/anon/${surveyNanoid}`, "GET"],
        ([path, method]) => fetcherSimple(path, method),
    );
    // The landing page establishes the respondent cookie; this fetch relies on it.
    const { data: survey, error: surveyError } = useSWR(
        () => [`/v2/response/survey`, "GET"],
        ([path, method]) => fetcherSimple(path, method),
    );
    const { data: status, error: statusError } = useSWR(
        [`/v2/response/status`, "GET"],
        ([path, method]) => fetcherSimple(path, method),
    );

    const [surveyProgress, setSurveyProgress] = useState(0);
    const [current, setCurrent] = useState(1);
    // Fetch existing responses + preset answers for the current question.
    const { data: questionResponse, error: questionResponseError } = useSWR(
        () => [
            survey && current && survey.questions[current - 1]
                ? "/v2/response/question/" +
                  survey.questions[current - 1].nanoid
                : null,
            "GET",
        ],
        ([path, method]) => fetcherSimple(path, method),
    );
    const { data: questionPresets, error: questionPresetsError } = useSWR(
        () => [
            survey &&
            current &&
            survey.questions[current - 1] &&
            (survey.questions[current - 1].question_type === "fixed" ||
                survey.questions[current - 1].question_type === "mixed")
                ? `/v2/response/question/${survey.questions[current - 1].nanoid}/preset`
                : null,
            "GET",
        ],
        ([path, method]) => fetcherSimple(path, method),
    );

    useEffect(() => {
        setPreviewModeChecked(true);
        if (previewMode) {
            setPreviewModeActive(true);
        }
    }, [previewMode]);

    // Layout metrics debug logging (mount + resize).
    useEffect(() => {
        const logLayoutMetrics = () => {
            const headerContainer = headerContainerRef.current;
            const rightImageContainer = rightImageContainerRef.current;

            if (headerContainer) {
                const headerRect = headerContainer.getBoundingClientRect();
                console.group('📐 Header Container Metrics');
                console.log('getBoundingClientRect:', {
                    x: headerRect.x,
                    y: headerRect.y,
                    width: headerRect.width,
                    height: headerRect.height,
                    top: headerRect.top,
                    right: headerRect.right,
                    bottom: headerRect.bottom,
                    left: headerRect.left,
                });
                console.log('scrollWidth:', headerContainer.scrollWidth);
                console.log('clientWidth:', headerContainer.clientWidth);
                console.log('offsetWidth:', headerContainer.offsetWidth);
                console.log('scrollHeight:', headerContainer.scrollHeight);
                console.log('clientHeight:', headerContainer.clientHeight);
                console.log('offsetHeight:', headerContainer.offsetHeight);
                console.log('Parent dimensions:', {
                    parentClientWidth: headerContainer.parentElement?.clientWidth,
                    parentScrollWidth: headerContainer.parentElement?.scrollWidth,
                    parentOffsetWidth: headerContainer.parentElement?.offsetWidth,
                });
                console.groupEnd();
            }

            if (rightImageContainer) {
                const imageRect = rightImageContainer.getBoundingClientRect();
                console.group('🖼️ Right Image Container Metrics');
                console.log('getBoundingClientRect:', {
                    x: imageRect.x,
                    y: imageRect.y,
                    width: imageRect.width,
                    height: imageRect.height,
                    top: imageRect.top,
                    right: imageRect.right,
                    bottom: imageRect.bottom,
                    left: imageRect.left,
                });
                console.log('scrollWidth:', rightImageContainer.scrollWidth);
                console.log('clientWidth:', rightImageContainer.clientWidth);
                console.log('offsetWidth:', rightImageContainer.offsetWidth);
                console.log('scrollHeight:', rightImageContainer.scrollHeight);
                console.log('clientHeight:', rightImageContainer.clientHeight);
                console.log('offsetHeight:', rightImageContainer.offsetHeight);
                console.log('Parent dimensions:', {
                    parentClientWidth: rightImageContainer.parentElement?.clientWidth,
                    parentScrollWidth: rightImageContainer.parentElement?.scrollWidth,
                    parentOffsetWidth: rightImageContainer.parentElement?.offsetWidth,
                });

                const viewportWidth = window.innerWidth;
                const isSpilling = imageRect.right > viewportWidth;
                console.log('Viewport Analysis:', {
                    viewportWidth,
                    imageRight: imageRect.right,
                    isSpillingOffScreen: isSpilling,
                    spillAmount: isSpilling ? imageRect.right - viewportWidth : 0,
                });
                console.groupEnd();
            }

            console.log('📊 Overall Viewport:', {
                viewportWidth: window.innerWidth,
                viewportHeight: window.innerHeight,
                documentWidth: document.documentElement.clientWidth,
                documentHeight: document.documentElement.clientHeight,
            });
        };

        logLayoutMetrics();

        const handleResize = () => {
            logLayoutMetrics();
        };

        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    useEffect(() => {
        if (
            status?.is_started === true &&
            status?.finished === false &&
            status?.survey?.state === "active"
        ) {
            setOpenSurvey(true);
        }
    }, [status]);

    useEffect(() => {
        if (survey) {
            survey.questions.sort((a, b) => {
                return a.position - b.position;
            });
        }
    }, [survey]);

    useEffect(() => {
        if (survey && current > 0) {
            const progress = ((current - 1) / survey.questions.length) * 100;
            const truncatedProgress = Math.trunc(progress);
            setSurveyProgress(truncatedProgress);
        }
    }, [survey, current]);

    const [questionSaved, setQuestionSaved] = useState(false);
    const [presetOther, setPresetOther] = useState(true);
    useEffect(() => {
        if (
            questionResponse &&
            (questionResponse.responses.preset.length > 0 ||
                questionResponse.responses.custom.length > 0)
        ) {
            setQuestionSaved(true);
        }
        if (survey) {
            const question = survey.questions[current - 1];
            if (
                questionResponse &&
                question.question_type === "mixed" &&
                question.answer_type === "single" &&
                questionResponse.responses.preset.length > 0
            ) {
                setPresetOther(false);
            }
        }
    }, [survey, questionResponse]);

    const [allValid, setAllValid] = useState(false);

    const nextQuestion = () => {
        const currentQuestion = survey.questions[current - 1];

        if (current === survey.questions.length) {
            setAnswers(questionResponse.responses);
            handleSurveySubmit();
        } else {
            setAnswers(questionResponse.responses);
            setCurrent((prevCurrent) => prevCurrent + 1);
            setQuestionSaved(false);
            setPresetOther(true);
            setValidationErrors(null);
        }
    };

    // POST current question's answers.
    const setAnswers = async (responses) => {
        if (previewModeActive) {
            return;
        }
        try {
            const result = await fetcherFull(
                `/v2/response/question/${survey.questions[current - 1].nanoid}`,
                "POST",
                {
                    preset: responses.preset.map((p) => p.nanoid),
                    custom: responses.custom.map((c) => c.nanoid),
                },
            );
            if (result.success) {
                mutateAnswers();
            } else {
                toast.error(
                    getApiErrorMessage(
                        result.status ?? classifyError(result),
                        "respond",
                    ),
                );
            }
        } catch (err) {
            toast.error(getApiErrorMessage(classifyError(err), "respond"));
        }
    };

    // Finalise the respondent's submission.
    const handleSurveySubmit = async () => {
        if (previewModeActive) {
            navigate(`/respond/${surveyNanoid}?previewThankYou=1`);
            return;
        }

        try {
            const finish = await fetcherFull("/v2/response/finish", "GET");
            if (finish.success) {
                navigate(`/respond/${surveyNanoid}`);
            } else {
                if (
                    finish.unanswered_questions &&
                    finish.unanswered_questions.length > 0
                ) {
                    setValidationErrors(finish.unanswered_questions);
                    toast.error(
                        `Please answer all required questions. ${finish.unanswered_questions.length} question(s) remaining.`
                    );
                    // Jump to the first unanswered question.
                    const firstUnanswered = finish.unanswered_questions[0];
                    const questionIndex = survey.questions.findIndex(
                        (q) => q.nanoid === firstUnanswered
                    );

                    if (questionIndex !== -1) {
                        setCurrent(questionIndex + 1);
                        setQuestionSaved(false);
                        setPresetOther(true);
                    }
                } else {
                    toast.error(
                        getApiErrorMessage(
                            finish.status ?? classifyError(finish),
                            "respond",
                        ),
                    );
                }
            }
        } catch (err) {
            toast.error(getApiErrorMessage(classifyError(err), "respond"));
        }
    };

    const { mutate } = useSWRConfig();

    // Invalidate the current question's SWR cache so a re-fetch picks up
    // the just-submitted answers.
    const mutateAnswers = () => {
        mutate(() => [
            survey && current && survey.questions[current - 1]
                ? "/v2/response/question/" +
                  survey.questions[current - 1].nanoid
                : null,
            "GET",
        ]);
    };

    return (
        <ResponseWrapper>
            <ContentWrapper hideBanners={true}>
                <>
                    {!!surveyError && (
                        <div>
                            There has been an error loading the survey that you
                            requested! Please check with the survey
                            administrator that you have been provided with a
                            valid url for an existing survey.
                        </div>
                    )}
                    {!!statusError && <div>Error loading survey status!</div>}
                    {!!(!previewMode && !openSurvey) && (
                        <>
                            <div className="flex flex-col md:max-w-[800px] mx-auto mt-4 bg-white rounded-xl sm:my-6 my-4">
                                <div className="py-4">
                                    <Alert
                                        color="red"
                                        className="mt-1 rounded-xl py-1 pl-5"
                                    >
                                        Survey is Disabled!
                                    </Alert>
                                </div>
                            </div>
                        </>
                    )}
                    {!!(
                        survey &&
                        (previewMode || openSurvey) &&
                        questionResponse &&
                        questionSaved !== undefined
                    ) && (
                        <div className="w-screen p-0 lg:px-20 sm:py-10 md:px-30 sm:px-10">
                            <div className="bg-white rounded-xl sm:rounded-2xl p-2 shadow-lg lg:w-[80%] xl:w-[70%] 2xl:w-[60%] sm:mx-auto m-1">
                                <div ref={headerContainerRef} className="flex flex-wrap items-center justify-between gap-2 p-2 md:flex-nowrap md:gap-4 md:px-4">
                                    <div className="flex items-center gap-2 min-w-0">
                                        {!!logoAvailable && (
                                            <Image
                                                alt="survey logo"
                                                src={
                                                    API_SERVER +
                                                    "/v2/response/logo"
                                                }
                                                className="w-[20vw] max-w-[20vw] h-auto max-h-24 object-contain mr-1 sm:mr-2"
                                                rounded={false}
                                                onError={() => {
                                                    setLogoAvailable(false);
                                                }}
                                            />
                                        )}
                                        <div className="min-w-0">
                                            <h1 className="md:text-2xl text-md mr-2 font-display text-black break-words">
                                                {survey.survey.name ||
                                                    "Loading Survey Name"}
                                            </h1>
                                        </div>
                                    </div>
                                    <div ref={rightImageContainerRef} className="ml-auto flex-shrink-0">
                                        <Image
                                            className="w-[20vw] max-w-[20vw] h-auto max-h-24 object-contain"
                                            src={UploadPicture}
                                            rounded={false}
                                            raised={false}
                                            alt="Gobby Logo"
                                        />
                                    </div>
                                    {/* {!!previewModeActive && (
                                        <div className="font-display text-xs sm:text-sm md:text-md text-red-700 rounded-full border border-red-900 text-center bg-white px-2 py-1 sm:px-3 md:px-5 flex-shrink-0 whitespace-nowrap">
                                            PREVIEW MODE!
                                        </div>
                                    )} */}
                                </div>
                                <div className="flex flex-row sm:px-4 px-2 bg-white gap-1 items-center md:w-[70%] m-auto md:mt-4">
                                    <Progress
                                        value={surveyProgress}
                                        className="border border-blue-gray-900 border rounded-full bg-white font-body"
                                        label="&nbsp;"
                                        variant="filled"
                                        color="blue-gray"
                                        size="lg"
                                    />
                                    <IconButton
                                        color="blue-gray"
                                        type="button"
                                        variant="filled"
                                        size="sm"
                                        disabled={current === 1}
                                        onClick={() => {
                                            setCurrent(
                                                (prevCurrent) =>
                                                    prevCurrent - 1,
                                            );
                                            setQuestionSaved(false);
                                            setValidationErrors(null);
                                        }}
                                    >
                                        <MaterialIcon
                                            name="arrow_back_ios_new"
                                            size="sm"
                                        />
                                    </IconButton>
                                    <IconButton
                                        color="blue-gray"
                                        type="button"
                                        variant="filled"
                                        size="sm"
                                        disabled={
                                            current ===
                                                survey.questions.length ||
                                            !(
                                                previewModeActive ||
                                                questionSaved
                                            )
                                        }
                                        onClick={nextQuestion}
                                    >
                                        <MaterialIcon
                                            name="arrow_forward_ios"
                                            size="sm"
                                        />
                                    </IconButton>
                                </div>
                                <div className="sm:px-4 sm:pt-4 p-2">
                                    <Card
                                        className="bg-transparent md:p-2 p-0"
                                        shadow={false}
                                    >
                                        <CardHeader
                                            variant="filled"
                                            className={`bg-white m-0 p-2 border ${
                                                validationErrors?.includes(
                                                    survey.questions[current - 1]?.nanoid
                                                )
                                                    ? "border-red-500"
                                                    : "border-blue-gray-100"
                                            }`}
                                            shadow={true}
                                            floated={false}
                                        >
                                            <Typography className="font-display text-black text-xl">
                                                Q{current}.&nbsp;&nbsp;
                                                {
                                                    survey.questions[
                                                        current - 1
                                                    ].text
                                                }
                                            </Typography>
                                            {validationErrors?.includes(
                                                survey.questions[current - 1]?.nanoid
                                            ) && (
                                                <Typography className="font-body mt-2 ml-1 text-red-500">
                                                    This question is required.
                                                </Typography>
                                            )}
                                            <Typography className="font-body mt-2 ml-1 text-black">
                                                {survey.questions[current - 1]
                                                    .description || ""}
                                            </Typography>
                                            {/* Optional Media Link - Temporarily disabled per issue #814 */}
                                            {/* {survey.questions[current-1].media_url && (
                                                <div className='my-3 text-center'>
                                                    <a
                                                        href={survey.questions[current-1].media_link}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        className='inline-block bg-blue-300 hover:bg-blue-600 text-black py-2 px-4 rounded-lg shadow-md transition-colors'
                                                    >
                                                        View Media
                                                    </a>
                                                </div>
                                            )} */}
                                        </CardHeader>
                                        <CardBody className="rounded-xl bg-white p-2 sm:p-4 mt-4 shadow  border border-blue-gray-100">
                                            {(() => {
                                                const question = survey.questions[current - 1];
                                                const isSingle = question.answer_type === "single";
                                                const isPresetOnly = question.question_type === "fixed";
                                                return (
                                                    <div className="font-body w-fit mx-auto px-8 py-2 rounded-xl bg-blue-50 mb-2 text-black">
                                                        <div className="font-semibold text-center mb-1">
                                                            {isSingle ? "Your response" : "Your response(s)"}
                                                        </div>
                                                        <ul className="text-sm space-y-1">
                                                            <li>
                                                                {isSingle
                                                                    ? "This question allows one response."
                                                                    : "This question allows multiple responses."}
                                                            </li>
                                                            {!isPresetOnly && (
                                                                <li>
                                                                    {question.is_view_all
                                                                        ? "Your responses could be matched by others."
                                                                        : "Your responses will not be matched with others."}
                                                                </li>
                                                            )}
                                                        </ul>
                                                    </div>
                                                );
                                            })()}
                                            <>
                                                {survey.questions[
                                                    current - 1
                                                ] &&
                                                    (survey.questions[
                                                        current - 1
                                                    ].question_type ===
                                                        "fixed" ||
                                                        survey.questions[
                                                            current - 1
                                                        ].question_type ===
                                                            "mixed") &&
                                                    questionPresets &&
                                                    questionPresets
                                                        .preset_answers.length >
                                                        0 && (
                                                    <PresetQuestionResponse
                                                        question={
                                                            survey
                                                                .questions[
                                                                    current - 1
                                                                ]
                                                        }
                                                        responses={
                                                            questionResponse.responses
                                                        }
                                                        update={
                                                            mutateAnswers
                                                        }
                                                        setAnswers={
                                                            setAnswers
                                                        }
                                                        setPresetOther={
                                                            setPresetOther
                                                        }
                                                        setQuestionSaved={
                                                            setQuestionSaved
                                                        }
                                                        savedStatus={
                                                            questionSaved
                                                        }
                                                        presetOther={
                                                            presetOther
                                                        }
                                                    />
                                                )}
                                                {survey.questions[
                                                    current - 1
                                                ] &&
                                                    (survey.questions[
                                                        current - 1
                                                    ].question_type ===
                                                        "freeform" ||
                                                        survey.questions[
                                                            current - 1
                                                        ].question_type ===
                                                            "mixed") && (
                                                    <CustomQuestionResponse
                                                        question={
                                                            survey
                                                                .questions[
                                                                    current - 1
                                                                ]
                                                        }
                                                        responses={
                                                            questionResponse.responses
                                                        }
                                                        update={
                                                            mutateAnswers
                                                        }
                                                        setAnswers={
                                                            setAnswers
                                                        }
                                                        setPresetOther={
                                                            setPresetOther
                                                        }
                                                        setQuestionSaved={
                                                            setQuestionSaved
                                                        }
                                                        savedStatus={
                                                            questionSaved
                                                        }
                                                        presetOther={
                                                            presetOther
                                                        }
                                                        previewModeState={
                                                            previewModeActive
                                                        }
                                                    />
                                                )}
                                            </>
                                        </CardBody>
                                        <CardFooter className="flex justify-center mt-3">
                                            <Button
                                                type="submit"
                                                className="btn btn--primary"
                                                size="lg"
                                                color={
                                                    questionSaved ||
                                                        previewModeActive
                                                        ? "green"
                                                        : "gray"
                                                }
                                                variant="filled"
                                                disabled={
                                                    !(
                                                        questionSaved ||
                                                            previewModeActive
                                                    )
                                                }
                                                // createCustomAnswer
                                                onClick={nextQuestion}
                                            >
                                                {questionSaved &&
                                                    current ===
                                                        survey.questions.length
                                                    ? "Submit answer and complete survey"
                                                    : "Next question"}
                                            </Button>
                                        </CardFooter>
                                    </Card>
                                </div>
                            </div>
                        </div>
                    )}
                </>
            </ContentWrapper>
        </ResponseWrapper>
    );
}
